import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { channelService, menuService } from '../api/services';
import { useBountyCount } from '../context/BountyCountContext';
import './Sidebar.css';

const Sidebar = ({ isOpen, onClose }) => {
  const [channels, setChannels] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [iframeUrl, setIframeUrl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { totalCount, mineCount } = useBountyCount();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [chRes, menuRes] = await Promise.all([
          channelService.list(),
          menuService.list(),
        ]);
        setChannels(chRes.data);
        setMenuItems(menuRes.data);
      } catch (err) {
        console.error('Sidebar fetch error:', err);
      }
    };
    fetchData();
  }, []);

  const currentChannel = new URLSearchParams(location.search).get('channel') || '';

  const handleChannelClick = (channelId) => {
    if (channelId) {
      navigate(`/?channel=${channelId}`);
    } else {
      navigate('/');
    }
    onClose?.();
  };

  const handleUserDirectoryClick = () => {
    navigate('/users');
    onClose?.();
  };

  const handleMenuClick = (item) => {
    if (item.action_type === 'SUBMENU') {
      setExpandedMenus(prev => ({ ...prev, [item.id]: !prev[item.id] }));
      return;
    }
    if (item.action_type === 'IFRAME') {
      setIframeUrl(item.action_value);
      return;
    }
    // LINK
    if (item.open_new_tab) {
      window.open(item.action_value, '_blank');
    } else {
      window.location.href = item.action_value;
    }
    onClose?.();
  };

  const renderMenuItem = (item, depth = 0) => (
    <div key={item.id}>
      <button
        className={`sidebar-menu-item depth-${depth}`}
        onClick={() => handleMenuClick(item)}
        title={item.action_value || item.title}
      >
        <span className="menu-icon">{item.icon}</span>
        <span className="menu-title">{item.title}</span>
        {item.action_type === 'SUBMENU' && (
          <span className={`menu-arrow ${expandedMenus[item.id] ? 'expanded' : ''}`}>▸</span>
        )}
        {item.action_type === 'IFRAME' && <span className="menu-badge">嵌入</span>}
        {item.action_type === 'LINK' && item.open_new_tab ? <span className="menu-badge">↗</span> : null}
      </button>
      {item.action_type === 'SUBMENU' && expandedMenus[item.id] && item.children?.length > 0 && (
        <div className="submenu">
          {item.children.map(child => renderMenuItem(child, depth + 1))}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className={`sidebar-overlay ${isOpen ? 'visible' : ''}`} onClick={onClose} />
      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h3>📋 導航</h3>
          <button className="sidebar-close" onClick={onClose}>✕</button>
        </div>

        <div className="sidebar-section">
          <h4 className="sidebar-section-title">頻道</h4>
          <button
            className={`sidebar-channel ${!currentChannel ? 'active' : ''}`}
            onClick={() => handleChannelClick('')}
          >
            <span className="channel-icon">🏠</span>
            <span>全部</span>
          </button>
          {channels.map(ch => (
            <button
              key={ch.id}
              className={`sidebar-channel ${currentChannel === ch.id ? 'active' : ''}`}
              onClick={() => handleChannelClick(ch.id)}
            >
              <span className="channel-icon">{ch.icon}</span>
              <span>{ch.name}</span>
            </button>
          ))}
        </div>

        <div className="sidebar-section">
          <h4 className="sidebar-section-title">人員</h4>
          <button
            className={`sidebar-channel ${location.pathname.startsWith('/users') ? 'active' : ''}`}
            onClick={handleUserDirectoryClick}
          >
            <span className="channel-icon">👥</span>
            <span>人員名錄</span>
          </button>
        </div>

        <div className="sidebar-section">
          <h4 className="sidebar-section-title">懸賞</h4>
          <button
            className={`sidebar-channel ${location.pathname === '/bounties' ? 'active' : ''}`}
            onClick={() => { navigate('/bounties'); onClose?.(); }}
          >
            <span className="channel-icon">🏆</span>
            <span>懸賞榜</span>
            {totalCount > 0 && <span className="sidebar-count-badge">{totalCount > 99 ? '99+' : totalCount}</span>}
          </button>
          <button
            className={`sidebar-channel ${location.pathname === '/bounties/mine' ? 'active' : ''}`}
            onClick={() => { navigate('/bounties/mine'); onClose?.(); }}
          >
            <span className="channel-icon">📥</span>
            <span>我領取的</span>
            {mineCount > 0 && <span className="sidebar-count-badge">{mineCount > 99 ? '99+' : mineCount}</span>}
          </button>
        </div>

        <div className="sidebar-section">
          <h4 className="sidebar-section-title">點數</h4>
          <button
            className={`sidebar-channel ${location.pathname === '/rewards' ? 'active' : ''}`}
            onClick={() => { navigate('/rewards'); onClose?.(); }}
          >
            <span className="channel-icon">🎁</span>
            <span>兌換商城</span>
          </button>
        </div>

        {menuItems.length > 0 && (
          <div className="sidebar-section">
            <h4 className="sidebar-section-title">功能表</h4>
            {menuItems.map(item => renderMenuItem(item))}
          </div>
        )}
      </aside>

      {/* Iframe Modal */}
      {iframeUrl && (
        <div className="iframe-modal-overlay" onClick={() => setIframeUrl(null)}>
          <div className="iframe-modal" onClick={e => e.stopPropagation()}>
            <div className="iframe-header">
              <span>{iframeUrl}</span>
              <button onClick={() => setIframeUrl(null)}>✕</button>
            </div>
            <iframe src={iframeUrl} title="Embedded Content" className="iframe-content" />
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;

